import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { formatThousandsSeparator } from 'helpers/others.helper'
import { Area } from 'graphqlAPI'
import { prefix } from '../constants'
import { CoursesProgress } from '../components'

export function useColumns() {
  const { t, i18n } = useTranslation()
  type Column = {
    title: string
    key: keyof Area | 'courses_advance_finish' | 'action'
    sorteable?: boolean
    width?: number
    hideable?: boolean
    render?: (...data: any) => any
  }

  const prefixHeaders = `${prefix}.headers`
  const prefixColumns = `${prefix}.columns`

  const withFormattedCount = useCallback(
    (translationKey: string, count: number) =>
      t(translationKey, { count }).replace(
        String(count),
        formatThousandsSeparator(count, i18n.language)
      ),
    [i18n.language, t]
  )

  const columns: Column[] = useMemo(
    () => [
      {
        title: t(`${prefixHeaders}.area`),
        key: 'name',
        sorteable: true,
        hideable: false,
        width: 70,
        render: (name) => {
          return (
            <div>
              {name} <span style={{ color: '#aaa9a9' }}></span>
            </div>
          )
        },
      },
      {
        title: t(`${prefixHeaders}.totalParticipants`),
        key: 'participantsCount',
        width: 170,
        sorteable: true,
        render: (value) => (
          <span>
            {withFormattedCount(`${prefixColumns}.participants`, value ?? 0)}
          </span>
        ),
      },
      {
        title: t(`${prefixHeaders}.courses`),
        key: 'coursesCount',
        width: 150,
        sorteable: true,
        render: (value) => {
          const count = value ?? 0
          const key =
            count === 1
              ? `${prefixColumns}.courses`
              : `${prefixColumns}.courses_plural`
          return <span>{withFormattedCount(key, count)}</span>
        },
      },
      {
        title: t(`${prefixHeaders}.progress`),
        key: 'courses_advance_finish',
        width: 120,
        sorteable: true,
        render: (_, { participantsProgress }: Area) => (
          <CoursesProgress
            progress={
              participantsProgress || { finish: 0, inProgress: 0, pending: 0 }
            }
          />
        ),
      },
      {
        title: t(`${prefixHeaders}.hours`),
        key: 'hoursTotal',
        width: 140,
        sorteable: true,
        render: (data) => (
          <span>
            {withFormattedCount(`${prefixColumns}.hoursTotal`, data ?? 0)}
          </span>
        ),
      },
      {
        title: '',
        key: 'action',
        width: 100,
        hideable: false,
        render: () => <span />,
      },
    ],
    [i18n.language, prefixColumns, prefixHeaders, t, withFormattedCount]
  )
  return columns
}
